import * as React from 'react';
import { FC, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import { FormStatus } from 'enums/support-form';
import { useRequestRefundMutation } from 'generated/rbi-graphql';
import { useOrderDetails } from 'hooks/use-order-details';
import { useSupportData } from 'hooks/use-support-data';
import AuthRequired from 'pages/authentication/auth-required';
import { StatusType, dataDogLogger } from 'utils/datadog';
import { routes } from 'utils/routing';

import ReportOrderNeverArrivedPageView from './report-order-never-arrived.view';
import { IReportOrderNeverArrivedItem, IReportOrderNeverArrivedPageViewProps } from './types';

export const NETWORK_ERROR = 'NETWORK_ERROR';
export const SUCCESSFUL_REFUND = 'SUCCESSFUL_REFUND';
export const FAILED_REFUND = 'FAILED_REFUND';

/**
 * ReportOrderNeverArrivedPage
 *
 * Used to report that an order was never received
 */

const ReportOrderNeverArrivedPage: FC = () => {
  const { formatMessage } = useIntl();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams({ categoryId: '' });
  const categoryId = searchParams.get('categoryId') ?? '';
  const { order, loading: orderLoading } = useOrderDetails(orderId);
  const [requestRefundMutation] = useRequestRefundMutation();
  const [formStatus, setFormStatus] = useState<FormStatus>(FormStatus.UNSENT);
  const [processingRefundRequest, setProcessingRefundRequest] = useState(false);
  const { deliveryNeverArriveSubcategory } = useSupportData();

  const issueId = deliveryNeverArriveSubcategory?.knowledgeForceIssueId || 'No Issue Id';

  const items: IReportOrderNeverArrivedItem[] =
    order?.cart.cartEntries.map(entry => ({
      id: entry.sanityId,
      quantity: entry.quantity,
      name: entry.name || 'missing item name',
    })) || [];

  const errorLinkQuery = new URLSearchParams({
    ...(categoryId && { categoryId }),
    ...(deliveryNeverArriveSubcategory?._id && {
      subcategoryId: deliveryNeverArriveSubcategory?._id,
    }),
    orderId,
  }).toString();

  const errorLink = `${routes.supportForm}?${errorLinkQuery}`;

  const language: IReportOrderNeverArrivedPageViewProps = {
    headingText: formatMessage({ id: 'orderNeverArrivedheading' }),
    subheadingText: formatMessage({ id: 'orderNeverArrivedsubheading' }),
    refundErrorText: formatMessage(
      { id: 'orderCannotBeRefundedMessage' },
      {
        link: (
          <Link to={errorLink} data-testid="refund-error-link">
            {formatMessage({ id: 'refundClickHere' })}
          </Link>
        ),
      }
    ),
    refundSuccessText: formatMessage({ id: 'orderHasBeenRefundedMessage' }),
    contentText: formatMessage({ id: 'orderNeverArrivedcontent' }),
    commentsPlaceholderText: formatMessage({ id: 'comments' }),
    submitButtonText: formatMessage({ id: 'orderNeverArrived' }),
  };

  useEffect(() => {
    // TODO: Implement redirects here when no order is found
    if (!orderLoading && !order) {
      dataDogLogger({
        message: 'Order Never Arrived Customer Initiated Refund Network Error: Order not found',
        status: StatusType.error,
      });
    }
  }, [orderLoading, order]);

  const handleFormStatus = (status: string) => {
    setFormStatus(FormStatus[status]);
  };

  const onSubmit: IReportOrderNeverArrivedPageViewProps['onSubmit'] = async fields => {
    try {
      setProcessingRefundRequest(true);
      const response = await requestRefundMutation({
        variables: {
          input: {
            rbiOrderId: order?.rbiOrderId || '',
            issueId,
            userComment: fields.comments,
          },
        },
      });
      if (response.data?.userRequestsRefund) {
        handleFormStatus(SUCCESSFUL_REFUND);
      } else {
        handleFormStatus(FAILED_REFUND);
      }
      setProcessingRefundRequest(false);
    } catch (e) {
      dataDogLogger({
        message: `Order Never Arrived Customer Initiated Refund Network Error: ${e}`,
        status: StatusType.error,
      });
      setProcessingRefundRequest(false);
      handleFormStatus(FAILED_REFUND);
    }
  };

  if (orderLoading) {
    return (
      <LoadingContainer>
        <LoadingAnimation fillColor={Styles.color.primary} />
      </LoadingContainer>
    );
  }

  return (
    <AuthRequired>
      <Helmet title={formatMessage({ id: 'orderNeverArrivedheading' })} />

      <ReportOrderNeverArrivedPageView
        isLoading={processingRefundRequest}
        items={items}
        onSubmit={onSubmit}
        disableSubmitButton={!order || formStatus !== FormStatus.UNSENT}
        onBack={() => window.history.back()}
        {...language}
        formStatus={formStatus}
      />
    </AuthRequired>
  );
};

export default ReportOrderNeverArrivedPage;
